import { type ElasticsearchQuery, type FacetFieldConfig } from '@searchkit/api';

import { horseRacingQuery, requiredFieldsQuery, validOddsQuery } from '~/apps/product-plus/search-domain/queries';
import {
  type SearchDomainContext,
  type SearchRuntimeSortingConfig,
  type SearchUiContext,
} from '~/apps/product-plus/search-domain/types';

export const premiumMarketMoversResultAttributes = [
  'betId',
  'betName',
  'bookmakerCode',
  'oddsAmerican',
  'oddsDecimal',
  'oddsFractional',
  'parentBetId',
  'betTypeId',
  'marketClassification',
  'marketDisplayName',
  'marketDisplayNameMap',
  'marketId',
  'marketName',
  'marketPropType',
  'categoryGroupId',
  'categoryGroupName',
  'categoryId',
  'categoryName',
  'eventDisplayName',
  'eventId',
  'shortestOdds',
  'subeventStartDate',
  'subeventStartTime',
  'horseRacingSportsData',
  'marketMovement',
];

export function getPremiumMarketMoversBaseFilters(): ElasticsearchQuery[] {
  return [horseRacingQuery, requiredFieldsQuery, validOddsQuery];
}

export function getPremiumMarketMoversFacetAttributes(context: SearchDomainContext): FacetFieldConfig[] {
  const areInternalSearchToolsEnabled = context.searchFlags.internalSearchTools;

  const attributes: FacetFieldConfig[] = [
    { attribute: 'categoryGroupName', field: 'categoryGroupName', type: 'string' },
    { attribute: 'eventDisplayName', field: 'eventDisplayName', type: 'string' },
    { attribute: 'eventId', field: 'eventId', type: 'numeric' },
    { attribute: 'horseRacingSportsData.runs', field: 'horseRacingSportsData.runs', type: 'numeric' },
    {
      attribute: 'horseRacingSportsData.distance.miles',
      field: 'horseRacingSportsData.distance.miles',
      type: 'numeric',
    },
    {
      attribute: 'horseRacingSportsData.distance.furlongs',
      field: 'horseRacingSportsData.distance.furlongs',
      type: 'numeric',
    },
    {
      attribute: 'horseRacingSportsData.form.placed.last1',
      field: 'horseRacingSportsData.form.placed.last1',
      type: 'string',
    },
    {
      attribute: 'horseRacingSportsData.form.placed.last3',
      field: 'horseRacingSportsData.form.placed.last3',
      type: 'string',
    },
    {
      attribute: 'horseRacingSportsData.form.placed.last6',
      field: 'horseRacingSportsData.form.placed.last6',
      type: 'string',
    },
    { attribute: 'marketDisplayNameMap.uk', field: 'marketDisplayNameMap.uk.keyword', type: 'string' },
    { attribute: 'oddsDecimal', field: 'oddsDecimal', type: 'numeric' },
    { attribute: 'oddsAmerican', field: 'oddsAmerican', type: 'numeric' },
    { attribute: 'oddsFractional', field: 'oddsFractional', type: 'numeric' },
    { attribute: 'subeventStartDate', field: 'subeventStartDate', type: 'string' },
    { attribute: 'marketMovement.swing.value', field: 'marketMovement.swing.value', type: 'numeric' },
    { attribute: 'marketMovement.swing.movement', field: 'marketMovement.swing.movement', type: 'string' },
    { attribute: 'marketMovement.swingPercentage', field: 'marketMovement.swingPercentage', type: 'numeric' },
    { attribute: 'horseRacingSportsData.jockey', field: 'horseRacingSportsData.jockey.keyword', type: 'string' },
    { attribute: 'horseRacingSportsData.trainer', field: 'horseRacingSportsData.trainer.keyword', type: 'string' },
    { attribute: 'horseRacingSportsData.owner', field: 'horseRacingSportsData.owner.keyword', type: 'string' },
    { attribute: 'horseRacingSportsData.horseName', field: 'horseRacingSportsData.horseName.keyword', type: 'string' },
    { attribute: 'horseRacingSportsData.handicap', field: 'horseRacingSportsData.handicap', type: 'string' },
  ];

  if (areInternalSearchToolsEnabled) {
    attributes.unshift({ attribute: 'bookmakerCode', field: 'bookmakerCode', type: 'string' });
  }

  return attributes;
}

export function getPremiumMarketMoversSearchSettings(context: SearchUiContext) {
  const searchAttributes = [
    'horseRacingSportsData.jockey',
    'horseRacingSportsData.trainer',
    'horseRacingSportsData.owner',
    'horseRacingSportsData.horseName',
  ];

  const searchRepubCountry = context.repubCountry;
  if (searchRepubCountry) {
    searchAttributes.unshift(`marketDisplayNameMap.${searchRepubCountry}`);
  }

  return {
    highlights: searchAttributes,
    results: premiumMarketMoversResultAttributes,
    search: searchAttributes,
  };
}

export function getPremiumMarketMoversSorting(): SearchRuntimeSortingConfig {
  return {
    options: [
      { field: 'marketMovement.swingPercentage', label: 'Steamers to Drifters', order: 'asc' },
      { field: 'marketMovement.swingPercentage', label: 'Drifters to Steamers', order: 'desc' },
      { field: 'subeventStartTime', label: 'Date & Time: Earliest', order: 'asc' },
      { field: 'subeventStartTime', label: 'Date & Time: Latest', order: 'desc' },
      { field: 'oddsDecimal', label: 'Best Odds: High to low', order: 'desc' },
      { field: 'oddsDecimal', label: 'Best Odds: Low to high', order: 'asc' },
    ],
  };
}
